import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import PropTypes from 'prop-types';
import { Input } from '@ipc/ipc-components';
import { useStyles } from './styles';

const SplitListSelect = ({
  options = [], allAllowed, disabled, selectChangeHandler, propertyName, values,
}) => {
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [checked, setChecked] = useState([]);
  const [leftFilter, setLeftFilter] = useState('');
  const [rightFilter, setRightFilter] = useState('');

  const not = (a, b) => a.filter(value => b.indexOf(value) === -1);
  const intersection = (a, b) => a.filter(value => b.indexOf(value) !== -1);
  const sortArray = array => array.sort((a, b) => (a.label > b.label ? 1 : -1));
  const filterItems = (items, filter) => items.filter(item => (item && item.label ? item.label.toLowerCase().includes(filter.toLowerCase()) : []));
  const filterArray = (items = [], itemsToExclude = []) => items.filter(item => !(new Set(itemsToExclude.filter(obj => obj).map(obj => obj.label)).has(item.label)));

  const classes = useStyles();
  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);
  const rightCache = new CellMeasurerCache({
    defaultHeight: 48,
    fixedWidth: true,
  });
  const leftCache = new CellMeasurerCache({
    defaultHeight: 48,
    fixedWidth: true,
  });

  useEffect(() => {
    const dynamicOptions = disabled ? values : options;
    const valuesChecked = values && values.hasOwnProperty(propertyName) ? values[propertyName] : [];
    const selectedProperties = !Array.isArray(valuesChecked) ? [valuesChecked] : valuesChecked;

    if (disabled) {
      setLeft(selectedProperties);
    } else {
      sortArray(dynamicOptions);
      setLeft(filterArray(dynamicOptions, selectedProperties));
      setRight(selectedProperties);
    }
  }, [values, options.length]);

  const handleToggle = value => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const isAllOption = () => {
    const [first = {}] = right;
    return first.label === 'All';
  };

  const handleAllOption = () => {
    const allOptions = { label: 'All', value: 'All' };
    setRight([allOptions]);
    selectChangeHandler([allOptions], { name: propertyName });
  };

  const handleAllRight = () => {
    const array = leftFilter !== '' ? filterItems(left, leftFilter) : left;
    setRight(sortArray(right.concat(array)));
    setLeftFilter('');
    setLeft(not(left, array));
    selectChangeHandler(array, { name: propertyName });
  };

  const handleAllLeft = () => {
    if (isAllOption()) {
      setRight([]);
      return;
    }

    const array = rightFilter !== '' ? filterItems(right, rightFilter) : right;
    setLeft(sortArray(left.concat(array)));
    setRightFilter('');
    setRight(not(right, array));
    selectChangeHandler([], { name: propertyName });
  };

  const handleCheckedRight = () => {
    const array = right.concat(leftChecked);
    setRight(sortArray(array));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
    selectChangeHandler(array, { name: propertyName });
  };

  const handleCheckedLeft = () => {
    if (isAllOption()) {
      setRight([]);
      return;
    }

    const array = left.concat(rightChecked);
    setLeft(sortArray(array));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
    selectChangeHandler(not(right, rightChecked), { name: propertyName });
  };

  const rowRenderer = (item, {
    style, key, index, parent,
  }, cache) => (
    (item
      && (
        <CellMeasurer
          cache={cache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          <div key={key} style={style}>
            <ListItem
              button
              onClick={handleToggle(item)}
              className={checked.indexOf(item) !== -1 ? classes.selected : classes.blue}
            >
              <Checkbox
                className={classes.checkbox}
                checked={checked.indexOf(item) !== -1}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText id={item.label} primary={item.label} />
            </ListItem>
          </div>
        </CellMeasurer>
      ))
  );

  const customList = (items, filter, setFilter, cache) => {
    const array = filterItems(items, filter);

    return (<>
      <Input
        name="filter"
        value={filter}
        placeholder="Search"
        className={classes.input}
        onChange={e => setFilter(e.target.value)}
      />
      <List
        deferredMeasurementCache={cache}
        height={300}
        width={200}
        rowHeight={cache.rowHeight}
        rowCount={array.length}
        className={classes.list}
        rowRenderer={prop => rowRenderer(array[prop.index], prop, cache)}
      />
      </>
    );
  };

  return (
    <>
      <Grid container spacing={2} justify="center" alignItems="center" className={classes.root}>
        <Grid item>{customList(left, leftFilter, setLeftFilter, leftCache)}</Grid>
        { !disabled
        && (
          <>
            <Grid item>
              <Grid container direction="column" alignItems="center">
                { allAllowed
                && (
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.button}
                    onClick={handleAllOption}
                    disabled={left.length === 0}
                    aria-label="all right"
                  >
                    All
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.button}
                  onClick={handleAllRight}
                  disabled={left.length === 0 || isAllOption()}
                  aria-label="move all right"
                >
                  ≫
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.button}
                  onClick={handleCheckedRight}
                  disabled={leftChecked.length === 0 || isAllOption()}
                  aria-label="move selected right"
                >
                  &gt;
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.button}
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                  aria-label="move selected left"
                >
                  &lt;
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className={classes.button}
                  onClick={handleAllLeft}
                  disabled={right.length === 0}
                  aria-label="move all left"
                >
                  ≪
                </Button>
              </Grid>
            </Grid>
            <Grid item>{customList(right, rightFilter, setRightFilter, rightCache)}</Grid>
          </>
        )}
      </Grid>
    </>
  );
};

SplitListSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape),
  allAllowed: PropTypes.bool,
  disabled: PropTypes.bool,
  values: PropTypes.shape(),
};

export default SplitListSelect;
